import React, { useEffect, useState } from "react";
import { Card, Row, Col, Typography } from "antd";
import {
  FaStore,
  FaTools,
  FaMobile,
  FaBell,
  FaUser,
  FaStamp,
  FaGift,
  FaGlobe,
  FaApple,
} from "react-icons/fa";
import useAuth from "../../hooks/useAuth";
import "./Home.css";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import EndpointFunctions from "../../utils/Functions/endpointFunctions";
import Endpoints from "../../api/endpoints";
import useSuccessErrorModal from "../../hooks/successErrorModal/useSuccessErrorModal";
const { Title, Paragraph } = Typography;

const Home = () => {
  const { showModal, closeModal, ModalComponent } = useSuccessErrorModal();

  const { auth } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const [statisticsData, setStatisticsData] = useState({
    customerCount: 0,
    stampCount: 0,
    rewardCount: 0,
    appRegistrationCount: 0,
    webRegistrationCount: 0,
    unknownRegistrationCount: 0,
    appleWalletActivePasses: 0,
  });
  const [isLoading, setIsLoading] = useState(false);

  const features = [
    {
      icon: <FaStore className="feature-icon" />,
      title: "Dükkan ve Müşteri Yönetimi",
      description:
        "Dükkan ve müşteri durumlarını görüntüleyebilir ve yönetebilirsiniz.",
    },
    {
      icon: <FaTools className="feature-icon" />,
      title: "Bakım Modu",
      description:
        "Uygulamanızı bakım moduna alarak gerekli güncellemeleri yapabilirsiniz.",
    },
    {
      icon: <FaMobile className="feature-icon" />,
      title: "Zorunlu Güncelleme",
      description:
        "Uygulama için zorunlu güncelleme ayarlarını yönetebilirsiniz.",
    },
    {
      icon: <FaBell className="feature-icon" />,
      title: "Bildirim Yönetimi",
      description:
        "Kullanıcılarınıza bildirim gönderebilir ve bildirim takiplerini yapabilirsiniz.",
    },
  ];

  const statistics = [
    {
      icon: <FaUser className="feature-icon" />,
      title: "Toplam Müşteri",
      value: statisticsData.customerCount,
    },
    {
      icon: <FaStamp className="feature-icon" />,
      title: "Toplam Damga",
      value: statisticsData.stampCount,
    },
    {
      icon: <FaGift className="feature-icon" />,
      title: "Toplam Ödül",
      value: statisticsData.rewardCount,
    },
  ];

  // Web katılım (app'siz) ve cüzdan benimseme metrikleri.
  const adoptionStats = [
    {
      icon: <FaGlobe className="feature-icon" />,
      title: "Web'den Kayıt",
      value: statisticsData.webRegistrationCount,
      hint: "web.mahalle.app üzerinden katılan müşteriler",
    },
    {
      icon: <FaMobile className="feature-icon" />,
      title: "Uygulamadan Kayıt",
      value: statisticsData.appRegistrationCount,
      hint: "Mobil uygulama üzerinden katılan müşteriler",
    },
    {
      icon: <FaApple className="feature-icon" />,
      title: "Apple Wallet",
      value: statisticsData.appleWalletActivePasses,
      hint: "Şu an cüzdanında aktif kartı olan müşteriler",
    },
  ];

  useEffect(() => {
    fetchStatisticsData();
  }, []);

  const fetchStatisticsData = async () => {
    setIsLoading(true);
    const result = await EndpointFunctions.fetchData({
      endpointURL: Endpoints.getStatistics,
      axiosPrivate: axiosPrivate,
    });

    if (result.success) {
      const statisticsData = result.data;
      setStatisticsData(statisticsData);
      setIsLoading(false);
    } else {
      showModal(result.errorTitle, result.errorMessage, () => {
        closeModal();
      });
    }
  };

  return (
    <div className="home-container">
      <Title level={2} className="welcome-title">
        Mahalle Kart Backoffice'e Hoşgeldiniz
      </Title>
      <Paragraph className="welcome-description">
        Bu yönetim paneli ile uygulamanızın tüm özelliklerini kolayca kontrol
        edebilirsiniz.
      </Paragraph>

      <div className="statistics-section">
        <Title level={3} style={{ marginBottom: "24px", textAlign: "center" }}>
          İstatistikler
        </Title>
        <Row gutter={[24, 24]} className="features-container">
          {statistics.map((stat, index) => (
            <Col xs={24} sm={12} md={8} lg={8} key={index}>
              <Card className="feature-card">
                <div className="icon-container">{stat.icon}</div>
                <Title level={4}>{stat.title}</Title>
                <Title level={2} style={{ color: "#1890ff" }}>
                  {stat.value}
                </Title>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      <div className="statistics-section" style={{ marginTop: "48px" }}>
        <Title level={3} style={{ marginBottom: "8px", textAlign: "center" }}>
          Web Katılım & Cüzdan
        </Title>
        <Paragraph
          type="secondary"
          style={{ textAlign: "center", marginBottom: "24px" }}>
          Kayıt kanalı yalnızca web katılım özelliğinden sonra kaydedildi; daha
          eski {statisticsData.unknownRegistrationCount} müşteri kanalsız
          (bilinmiyor) sayılır.
        </Paragraph>
        <Row gutter={[24, 24]} className="features-container">
          {adoptionStats.map((stat, index) => (
            <Col xs={24} sm={12} md={8} lg={8} key={index}>
              <Card className="feature-card">
                <div className="icon-container">{stat.icon}</div>
                <Title level={4}>{stat.title}</Title>
                <Title level={2} style={{ color: "#1890ff" }}>
                  {stat.value}
                </Title>
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  {stat.hint}
                </Paragraph>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      <Title
        level={3}
        style={{
          marginTop: "48px",
          marginBottom: "24px",
          textAlign: "center",
        }}>
        Özellikler
      </Title>
      <Row gutter={[24, 24]} className="features-container">
        {features.map((feature, index) => (
          <Col xs={24} sm={12} md={12} lg={6} key={index}>
            <Card className="feature-card">
              <div className="icon-container">{feature.icon}</div>
              <Title level={4}>{feature.title}</Title>
              <Paragraph>{feature.description}</Paragraph>
            </Card>
          </Col>
        ))}
      </Row>
      {ModalComponent}
    </div>
  );
};

export default Home;
